// /opt/ptg01-v1/front-admin/components/SplitView.tsx
// Composant unifié — col 1 (liste 320px) + zone droite (detail 2-col OU col2+col3 3-col).
// Remplace l'ancien SplitView3 : si col2+col3 sont passés, mode 3 colonnes ; sinon mode 2 colonnes.
// Gabarit C · 4 colonnes (CONVENTIONS § 6, décision du propriétaire du 2026-10-03, /espace-porte seulement) : si navCol +
// pageCol sont passés, col 2 = menu de largeur fixe (GABARIT_C_NAV_WIDTH) et col 3 + col 4 = une seule page.
'use client';

import type { ReactNode } from 'react';

// Largeur standard col 1 dans tout le front-admin. Conserver synchronisé.
export const ADMIN_LIST_WIDTH = 320;

/** Gabarit C : largeur de la col 2 = sidebar de l'extranet du porté (front-extranet/components/Sidebar.tsx, 260 px). */
export const GABARIT_C_NAV_WIDTH = 260;

export default function SplitView({
    listWidth = ADMIN_LIST_WIDTH,
    list,
    pageHeader,
    detail,
    emptyState,
    hasSelection = false,
    col2,
    col3,
    col2Fraction = 1,
    col3Fraction = 1,
    navCol,
    pageCol,
    bg = 'bg-white',
}: {
    /** Largeur fixe col 1 (défaut : 320, standard front-admin). */
    listWidth?: number;
    /** Col 1 : liste. */
    list: ReactNode;
    /** Bandeau au-dessus de col 2 (mode 2-col) ou col 2+3 (mode 3-col). */
    pageHeader?: ReactNode;
    /** Mode 2-col : contenu affiché si hasSelection. */
    detail?: ReactNode;
    /** Mode 2-col : fallback si !hasSelection. */
    emptyState?: ReactNode;
    /** Mode 2-col : contrôle l'affichage de detail vs emptyState. */
    hasSelection?: boolean;
    /** Mode 3-col : col 2 (ignorée si detail présent seul). */
    col2?: ReactNode;
    /** Mode 3-col : col 3. */
    col3?: ReactNode;
    col2Fraction?: number;
    col3Fraction?: number;
    /** Gabarit C : col 2, menu de largeur fixe (GABARIT_C_NAV_WIDTH). */
    navCol?: ReactNode;
    /** Gabarit C : col 3 + col 4 réunies (une page). */
    pageCol?: ReactNode;
    /** Background de la zone entière. */
    bg?: string;
}) {
    const is3Col = col2 !== undefined && col3 !== undefined;
    const isC = navCol !== undefined && pageCol !== undefined;

    return (
        <div className={`flex h-[calc(100vh-3.5rem)] ${bg}`}>
            {/* COL 1 — liste */}
            <div
                style={{ width: listWidth, flex: 'none' }}
                className="border-r border-slate-200 bg-white flex flex-col overflow-hidden min-w-0"
            >
                {list}
            </div>

            {/* Zone droite : pageHeader optionnel + corps (detail OU col 2+3) */}
            <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                {pageHeader && (
                    <div className="border-b border-slate-200 bg-white px-6 py-4 shrink-0">
                        {pageHeader}
                    </div>
                )}
                {isC ? (
                    <div className="flex-1 flex overflow-hidden min-w-0">
                        <div
                            style={{ width: GABARIT_C_NAV_WIDTH, flex: 'none' }}
                            className="border-r border-slate-200 bg-white flex flex-col overflow-hidden min-w-0"
                        >
                            {navCol}
                        </div>
                        <div className="flex-1 overflow-hidden bg-white min-w-0">
                            {pageCol}
                        </div>
                    </div>
                ) : is3Col ? (
                    <div className="flex-1 flex overflow-hidden">
                        {/* Zones défilables focalisables au clavier (axe : scrollable-region-focusable). */}
                        <div
                            tabIndex={0}
                            role="region"
                            aria-label="Colonne centrale"
                            style={{ flex: `${col2Fraction} 1 0` }}
                            className="overflow-y-auto border-r border-slate-200 min-w-0"
                        >
                            {col2}
                        </div>
                        <div
                            tabIndex={0}
                            role="region"
                            aria-label="Colonne de détail"
                            style={{ flex: `${col3Fraction} 1 0` }}
                            className="overflow-y-auto min-w-0"
                        >
                            {col3}
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 overflow-hidden bg-white min-w-0">
                        {hasSelection ? detail : (emptyState || <DefaultEmptyState />)}
                    </div>
                )}
            </div>
        </div>
    );
}

function DefaultEmptyState() {
    return (
        <div className="h-full flex flex-col items-center justify-center text-center px-8">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <svg className="w-5 h-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
            </div>
            <p className="text-[13px] text-slate-600">Sélectionnez un élément</p>
            <p className="text-[11px] text-slate-600 mt-1">Cliquez sur une ligne pour afficher le détail</p>
        </div>
    );
}
